import PropTypes from "prop-types"
import React, { useRef } from "react"
import { Controller, useFormContext } from "react-hook-form"

import ArchivedContactRestorePanel from "src/components/ArchivedContactRestorePanel"
import Button from "src/components/Button"
import ContactAutocomplete from "src/components/ContactAutocomplete"
import ContactBoatAutocomplete from "src/components/ContactBoatAutocomplete"
import Form from "src/components/Form"

import BoatFieldGroup from "./BoatFieldGroup"
import BoatSummaryChip from "./BoatSummaryChip"
import ContactFieldGroup from "./ContactFieldGroup"
import ContactSummaryChip from "./ContactSummaryChip"
import {
  PANEL_ARCHIVED,
  PANEL_CREATE_BOAT,
  PANEL_CREATE_BOTH,
  PANEL_NONE,
} from "./useContactBoatPicker"

const SectionHeader = ({ title, action }) => (
  <div className="flex items-center justify-between">
    <h3 className="text-base font-semibold text-gray-900">{title}</h3>
    {action}
  </div>
)

SectionHeader.propTypes = {
  title: PropTypes.string.isRequired,
  action: PropTypes.node,
}

const ContactBoatPicker = ({
  handle,
  enableImport = false,
  enableGuest = false,
  guestLabel = "Guest",
  extraFields = [],
}) => {
  const {
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext()
  const {
    panel,
    archivedMatches,
    selectedContactName,
    openCreateBoth,
    openCreateBoat,
    openArchivedFor,
    cancelPanel,
    clearSelectedContactName,
    handleArchivedRestored,
    setIsGuest,
    clearIsGuest,
  } = handle

  // Ref keeps the contact_id validation rule reactive to panel changes.
  const panelRef = useRef(panel)
  panelRef.current = panel

  const [selectedContact, selectedBoat, contactId, contactBoatId, isGuest] =
    watch([
      "contact",
      "contact_boat",
      "contact_id",
      "contact_boat_id",
      "is_guest",
    ])
  const showContactChip =
    panel === PANEL_NONE && Boolean(contactId) && Boolean(selectedContact?.name)
  const showGuestChip = panel === PANEL_NONE && Boolean(isGuest)
  const showBoatChip =
    panel === PANEL_NONE &&
    Boolean(contactBoatId) &&
    Boolean(selectedBoat?.name)

  const handleContactRemove = () => {
    setValue("contact_id", "", { shouldDirty: true })
    setValue("contact", {}, { shouldDirty: true })
    setValue("contact_boat_id", "", { shouldDirty: true })
    setValue("contact_boat", {}, { shouldDirty: true })
    clearSelectedContactName?.()
  }

  const handleBoatRemove = () => {
    setValue("contact_boat_id", "", { shouldDirty: true })
    setValue("contact_boat", {}, { shouldDirty: true })
  }

  if (panel === PANEL_ARCHIVED && archivedMatches.length > 0) {
    return (
      <ArchivedContactRestorePanel
        archivedContacts={archivedMatches}
        onRestored={handleArchivedRestored}
        onCancel={cancelPanel}
      />
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <section>
        {panel === PANEL_CREATE_BOTH ? (
          <>
            <SectionHeader
              title="Customer"
              action={
                <Button variant="link" small onClick={cancelPanel}>
                  Back to customer search
                </Button>
              }
            />
            <ContactFieldGroup />
          </>
        ) : (
          <div>
            <Form.Label htmlFor="contact-boat-picker-contact" required>
              Search customers
            </Form.Label>
            <Controller
              name="contact_id"
              control={control}
              rules={{
                validate: (value) =>
                  panelRef.current === PANEL_CREATE_BOTH ||
                  (handle.enableGuest && watch("is_guest")) ||
                  !!value ||
                  "Please select a customer",
              }}
              render={({ field: { onChange, value } }) => (
                <ContactAutocomplete
                  id="contact-boat-picker-contact"
                  value={value}
                  onChange={(newId, contact) => {
                    onChange(newId)
                    if (contact) {
                      setValue("contact", contact, { shouldDirty: true })
                    }
                    if (selectedContactName) clearSelectedContactName?.()
                  }}
                  onCreateNew={openCreateBoth}
                  onArchivedSelect={(contact) => openArchivedFor([contact])}
                  displayName={selectedContactName || selectedContact?.name}
                  enableImport={enableImport}
                  extraFields={extraFields}
                  guestRow={
                    enableGuest
                      ? { label: guestLabel, onSelect: setIsGuest }
                      : undefined
                  }
                  disabled={showContactChip || showGuestChip}
                />
              )}
            />
            {errors.contact_id && (
              <Form.Error>{errors.contact_id.message}</Form.Error>
            )}
            {showContactChip && (
              <ContactSummaryChip
                contact={selectedContact}
                onRemove={handleContactRemove}
              />
            )}
            {showGuestChip && (
              <ContactSummaryChip
                contact={{ name: guestLabel }}
                onRemove={clearIsGuest}
              />
            )}
          </div>
        )}
      </section>
      <section>
        {panel === PANEL_NONE && (
          <div>
            <Form.Label
              htmlFor="contact-boat-picker-boat"
              required={handle.requireBoat !== false}
            >
              {contactId ? "Select a boat" : "Search boats"}
            </Form.Label>
            <ContactBoatAutocomplete
              name="contact_boat_id"
              id="contact-boat-picker-boat"
              onAddBoat={showBoatChip ? undefined : openCreateBoat}
              required={handle.requireBoat !== false}
              allowBoatFirst={!!handle.allowBoatFirst}
              disabled={showBoatChip || showGuestChip}
              extraFields={extraFields}
            />
            {showBoatChip && (
              <BoatSummaryChip
                boat={selectedBoat}
                onRemove={handleBoatRemove}
              />
            )}
          </div>
        )}
        {panel === PANEL_CREATE_BOAT && (
          <>
            <SectionHeader
              title="Boat"
              action={
                <Button variant="link" small onClick={cancelPanel}>
                  Select existing boat instead
                </Button>
              }
            />
            <BoatFieldGroup />
          </>
        )}
        {panel === PANEL_CREATE_BOTH && (
          <>
            <SectionHeader title="Boat" />
            {handle.requireBoat === false && (
              <div className="mb-2">
                <Form.Checkbox
                  name="picker-include-boat-in-create"
                  checked={!!handle.includeBoatInCreate}
                  onChange={(e) =>
                    handle.setIncludeBoatInCreate?.(e.target.checked)
                  }
                  compact
                >
                  Also add a boat
                </Form.Checkbox>
              </div>
            )}
            {(handle.requireBoat !== false || handle.includeBoatInCreate) && (
              <BoatFieldGroup />
            )}
          </>
        )}
      </section>
    </div>
  )
}

ContactBoatPicker.propTypes = {
  handle: PropTypes.shape({
    panel: PropTypes.string.isRequired,
    archivedMatches: PropTypes.array.isRequired,
    selectedContactName: PropTypes.string,
    requireBoat: PropTypes.bool,
    enableGuest: PropTypes.bool,
    allowBoatFirst: PropTypes.bool,
    includeBoatInCreate: PropTypes.bool,
    setIncludeBoatInCreate: PropTypes.func,
    openCreateBoth: PropTypes.func.isRequired,
    openCreateBoat: PropTypes.func.isRequired,
    openArchivedFor: PropTypes.func.isRequired,
    cancelPanel: PropTypes.func.isRequired,
    clearSelectedContactName: PropTypes.func,
    handleArchivedRestored: PropTypes.func.isRequired,
    setIsGuest: PropTypes.func,
    clearIsGuest: PropTypes.func,
  }).isRequired,
  enableImport: PropTypes.bool,
  enableGuest: PropTypes.bool,
  guestLabel: PropTypes.string,
  extraFields: PropTypes.arrayOf(PropTypes.string),
}

export default ContactBoatPicker
